import * as Sentry from '@sentry/react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppContextProvider } from './app/providers/AppContextProvider';
import BasePage from './components/layout/BasePage';
import Layout from './components/layout/Layout';
import { ProgressStepProvider } from './contexts/ProgressStepProvider';
import Application from './features/Application';
import InBehandelingPage from './features/InBehandeling/InBehandelingPage';
import Overview from './features/Overview';
import Policy from './features/Policy';
import PolicyUnreadablePage from './features/Policy/components/UnreadablePage';
import ProductSelection from './features/ProductSelection';
import Quote from './features/Quote';
import QuoteForwarder from './features/Quote/Forwarder';
import LinkExpired from './features/Quote/LinkExpired';
import RequestOverview from './features/RequestOverview';
import { useStatusRouting } from './hooks/useStatusRouting';
import { MAINTENANCE_MODE } from './lib/maintenance';
import type { ConfigData } from './types/app';

interface FnlConfig {
	currentConfig: ConfigData;
}

interface AppProps {
	fnlConfig: FnlConfig;
}

/**
 * Read-only request overview: the landing page for the link we mail out, and the
 * forwarder target that first exchanges the token. Shared so both stay in sync.
 */
const DOSSIER_BASE_PATH = '/dossier';

const StatusRoute = (): null => {
	useStatusRouting();
	return null;
};

const App = ({ fnlConfig }: AppProps): React.JSX.Element => {
	const { t } = useTranslation();

	return (
		<Sentry.ErrorBoundary fallback={<p>{t('errors.errorOccurred')}</p>}>
			<AppContextProvider fnlConfig={fnlConfig}>
				<ProgressStepProvider>
					<Layout>
						{MAINTENANCE_MODE ? (
							<Routes>
								<Route path="/" element={<Policy />} />
								<Route path="*" element={<Navigate to="/" replace />} />
							</Routes>
						) : (
							<Routes>
								<Route path="/" element={<Policy />} />
								<Route path="*" element={<BasePage is404={true} />} />
								<Route path="/-/:id" element={<StatusRoute />} />
								<Route path="/aanbod/:id" element={<Quote />} />
								<Route path="/aanbod/:id/:token" element={<QuoteForwarder />} />
								<Route path="/aanvulling/:id/:selected" element={<ProductSelection />} />
								<Route path="/overzicht/:id/:selected" element={<Overview />} />
								<Route path={`${DOSSIER_BASE_PATH}/:id`} element={<RequestOverview />} />
								<Route
									path={`${DOSSIER_BASE_PATH}/:id/:token`}
									element={<QuoteForwarder destination={DOSSIER_BASE_PATH} />}
								/>
								<Route path="/aanvraag/:id/:selected/:step?" element={<Application />} />
								<Route path="/link-verlopen/:id" element={<LinkExpired />} />
								<Route path="/polis-niet-leesbaar" element={<PolicyUnreadablePage />} />
								<Route path="/inbehandeling/:id" element={<InBehandelingPage />} />
								<Route
									path="/oeps"
									element={
										<BasePage
											title={t('errors.policyReadError')}
											description={t('common.policyReadErrorDescription')}
											showContactButton={true}
										/>
									}
								/>
							</Routes>
						)}
					</Layout>
				</ProgressStepProvider>
			</AppContextProvider>
		</Sentry.ErrorBoundary>
	);
};

export default App;
